
import { defineComponent } from "vue"
import {
    MessengerNotificationTracker,
    MessengerChatNotification,
} from "@/js/utils/messengerNotifications"
import {
    enableMessengerPush,
    restoreMessengerPush,
    disableMessengerPush,
} from "@/js/utils/messengerPush"

export default defineComponent({
    name: "MessengerNotifications",
    props: {
        collapsed: { type: Boolean, default: false },
    },
    emits: ["unread-change", "open-messenger"],
    data: () => ({
        tracker: new MessengerNotificationTracker(),
        toastVisible: false,
        unavailable: false,
        permission: "Notification" in window ? Notification.permission : "unsupported",
        audio: null as HTMLAudioElement | null,
        soundEnabled: true,
        soundReady: false,
        soundUnlocking: null as Promise<void> | null,
        pushEnabled: false,
        pushBusy: false,
        pushError: false,
        timer: 0,
        toastTimer: 0,
        controller: null as AbortController | null,
        stopped: false,
        notification: null as Notification | null,
        originalTitle: document.title,
    }),
    computed: {
        buttonTitle(): string {
            if (this.pushBusy) return "Настройка уведомлений…"
            if (this.pushEnabled) return "Выключить уведомления чатов"
            if (this.pushError) return "Повторить подключение уведомлений"
            return "Включить уведомления чатов"
        },
    },
    mounted() {
        try {
            this.soundEnabled = window.localStorage.getItem("messenger.push.disabled") !== "1"
        } catch {
            // Sound stays enabled when browser storage is unavailable.
        }
        this.ensureAudio()
        document.addEventListener("click", this.unlockSound)
        document.addEventListener("keydown", this.unlockSound)
        this.poll()
        this.pushBusy = true
        restoreMessengerPush()
            .then((enabled) => {
                if (!this.stopped) this.pushEnabled = enabled
            })
            .catch(() => {
                if (!this.stopped) this.pushError = true
            })
            .finally(() => {
                if (!this.stopped) this.pushBusy = false
            })
        window.addEventListener("focus", this.refresh)
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.addEventListener("message", this.handlePushMessage)
        }
    },
    beforeUnmount() {
        this.stopped = true
        window.clearTimeout(this.timer)
        window.clearTimeout(this.toastTimer)
        window.removeEventListener("focus", this.refresh)
        document.removeEventListener("click", this.unlockSound)
        document.removeEventListener("keydown", this.unlockSound)
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.removeEventListener("message", this.handlePushMessage)
        }
        this.controller?.abort()
        this.notification?.close()
        this.audio?.pause()
        document.title = this.originalTitle
    },
    methods: {
        async enableNotifications() {
            if (this.pushBusy) return
            this.pushError = false
            if (this.pushEnabled) {
                this.soundEnabled = false
                this.audio?.pause()
                this.pushBusy = true
                try {
                    await disableMessengerPush()
                    this.pushEnabled = false
                } catch {
                    this.pushError = true
                } finally {
                    this.pushBusy = false
                }
                return
            }
            // Browser permission and page audio are independent settings.
            let permission = Promise.resolve(this.permission)
            if ("Notification" in window && window.isSecureContext) {
                if (Notification.permission === "default") {
                    permission = Notification.requestPermission().catch(
                        () => Notification.permission,
                    )
                } else {
                    this.permission = Notification.permission
                    permission = Promise.resolve(this.permission)
                }
            }
            this.pushBusy = true
            this.soundEnabled = true
            this.unlockSound()
            try {
                this.permission = await permission
                if (this.permission === "granted") this.pushEnabled = await enableMessengerPush()
            } catch {
                this.pushError = true
            } finally {
                this.pushBusy = false
            }
        },
        ensureAudio(): HTMLAudioElement {
            if (!this.audio) {
                this.audio = new Audio("/sounds/messenger.wav")
                this.audio.preload = "auto"
                this.audio.volume = 0.85
            }
            return this.audio
        },
        unlockSound() {
            if (!this.soundEnabled || this.soundReady || this.soundUnlocking || this.stopped) return
            const audio = this.ensureAudio()
            if (!audio.paused) return
            audio.volume = 0
            // Activate the same audio element from a real click/key press for later background messages.
            this.soundUnlocking = audio
                .play()
                .then(() => {
                    audio.pause()
                    audio.currentTime = 0
                    if (!this.stopped) this.soundReady = true
                })
                .catch(() => {
                    if (!this.stopped) this.soundReady = false
                })
                .finally(() => {
                    audio.volume = 0.85
                    this.soundUnlocking = null
                })
        },
        async playSound() {
            if (!this.soundEnabled || this.stopped) return
            if (this.soundUnlocking) await this.soundUnlocking
            if (!this.soundEnabled || this.stopped) return
            const audio = this.ensureAudio()
            if (!audio.paused) return
            audio.currentTime = 0
            try {
                await audio.play()
                if (!this.stopped) this.soundReady = true
            } catch {
                if (!this.stopped) this.soundReady = false
            }
        },
        async poll() {
            if (this.stopped || this.controller) return
            this.controller = new AbortController()
            const timeout = window.setTimeout(() => this.controller?.abort(), 20000)
            let delay = 10000
            try {
                const response = await fetch("/messenger/notifications", {
                    credentials: "same-origin",
                    headers: { Accept: "application/json" },
                    cache: "no-store",
                    signal: this.controller.signal,
                })
                if (this.stopped) return
                if ([401, 403].includes(response.status)) {
                    this.stopped = true
                    this.tracker.update([])
                    this.$emit("unread-change", 0)
                    document.title = this.originalTitle
                    this.hideToast()
                    throw new Error("Notifications are not authorized")
                }
                if (!response.ok) throw new Error("Notifications are unavailable")
                const snapshot: { chats: MessengerChatNotification[] } = await response.json()
                if (this.stopped) return
                const { unreadCount, hasNewMessages } = this.tracker.update(snapshot.chats)
                this.unavailable = false
                this.$emit("unread-change", unreadCount)
                document.title = unreadCount
                    ? `(${unreadCount}) Чаты — ${this.originalTitle}`
                    : this.originalTitle
                if (!unreadCount) this.hideToast()
                if (hasNewMessages) this.notify()
            } catch {
                if (!this.stopped) this.unavailable = true
                delay = 30000
            } finally {
                window.clearTimeout(timeout)
                this.controller = null
                if (!this.stopped) this.timer = window.setTimeout(this.poll, delay)
            }
        },
        refresh() {
            window.clearTimeout(this.timer)
            this.poll()
        },
        handlePushMessage(event: MessageEvent) {
            if (event.data?.type === "messenger:refresh") {
                event.ports?.[0]?.postMessage({ soundReady: this.soundEnabled && this.soundReady })
                event.ports?.[0]?.close()
                this.refresh()
            }
        },
        notify() {
            this.toastVisible = true
            window.clearTimeout(this.toastTimer)
            this.toastTimer = window.setTimeout(() => (this.toastVisible = false), 8000)
            this.playSound()
            if (this.pushEnabled) return
            if (!("Notification" in window) || Notification.permission !== "granted") return
            try {
                this.notification?.close()
                this.notification = new Notification("Чаты Askom", {
                    body: "Новое сообщение от клиента. Откройте чаты на портале, чтобы ответить.",
                    icon: "/favicons/android-chrome-512x512.png",
                    requireInteraction: true,
                    silent: true,
                })
                this.notification.onclick = () => {
                    window.focus()
                    this.openMessenger()
                }
            } catch {
                // Some browsers only support service worker notifications.
            }
        },
        hideToast() {
            this.toastVisible = false
            this.notification?.close()
        },
        openMessenger() {
            this.hideToast()
            this.$emit("open-messenger")
        },
    },
})
